import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {(event: Event) => void} | undefined */
  #handleGlobalVariantUpdateRef;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));

    // Listen for variant updates to reinitialize Swiper globally
    this.#handleGlobalVariantUpdateRef = this.handleGlobalVariantUpdate.bind(this);
    document.addEventListener('variant:update', this.#handleGlobalVariantUpdateRef);

    this.#captureSelectedState();
    this.#refreshOptionLabels();
    this.#syncSizeControlAttributes();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up global event listener
    if (this.#handleGlobalVariantUpdateRef) {
      document.removeEventListener('variant:update', this.#handleGlobalVariantUpdateRef);
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Swatch guardrails (align quick-add/PDP swatches with product-card behavior):
    // If a swatch input carries availability metadata, pivot to the first available
    // variant for that swatch so color remains stable even when current size is invalid.
    if (event.target instanceof HTMLInputElement) {
      const clicked = event.target;
      const availableCountStr = clicked.dataset.availableCount;
      const firstAvailId = clicked.dataset.firstAvailableOrFirstVariantId;

      if (typeof availableCountStr !== 'undefined' && firstAvailId) {
        const availableCount = parseInt(availableCountStr || '0', 10);
        if (availableCount > 0) {
          // Prevent default flow and fetch by explicit variant id instead of option_values
          event.stopPropagation();

          // Visually mark selection
          try {
            this.updateSelectedOption(clicked);
          } catch (_) { }
          const optionValueId = clicked.dataset.optionValueId || '';

          // Build request URL - try to preserve currently selected size
          let productUrl = this.dataset.productUrl || '';
          if (productUrl.includes('?')) productUrl = productUrl.split('?')[0];
          const url = new URL(productUrl || window.location.pathname, window.location.origin);

          // Get all currently selected option value IDs from other option groups
          const allSelectedOptionValueIds = [];
          allSelectedOptionValueIds.push(optionValueId); // Add the clicked swatch's option value ID

          // Find other selected options (e.g., size) and add them
          const clickedFieldset = clicked.closest('fieldset');
          const allFieldsets = this.querySelectorAll('fieldset.variant-option');
          allFieldsets.forEach((fieldset) => {
            if (fieldset === clickedFieldset) return; // Skip the clicked swatch's fieldset
            const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
            if (checkedInput instanceof HTMLInputElement && checkedInput.dataset.optionValueId) {
              allSelectedOptionValueIds.push(checkedInput.dataset.optionValueId);
            }
            const selectEl = fieldset.querySelector('select');
            if (selectEl instanceof HTMLSelectElement && selectEl.value) {
              const selectedOption = selectEl.options[selectEl.selectedIndex];
              if (selectedOption?.dataset?.optionValueId) {
                allSelectedOptionValueIds.push(selectedOption.dataset.optionValueId);
              }
            }
          });

          // Persist ALL selected options (color + size) so they survive the morph
          if (optionValueId) {
            this.dataset.currentSelectedOptionId = optionValueId;
            // Store ALL selected options, not just the clicked one
            this.dataset.currentSelectedOptions = allSelectedOptionValueIds.join(',');
            this.dispatchEvent(new VariantSelectedEvent({ id: optionValueId }));
          }

          // Use option_values to let the server find the best matching variant
          // This preserves the currently selected size when switching colors
          if (allSelectedOptionValueIds.length > 1) {
            url.searchParams.set('option_values', allSelectedOptionValueIds.join(','));
          } else {
            // Fallback to first available variant if no other options are selected
            url.searchParams.set('variant', firstAvailId);
          }

          // Inside quick-add or swatches, request the product-card section for consistent partial updates
          if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
            url.searchParams.set('section_id', 'section-rendering-product-card');
          }

          this.fetchUpdatedSection(url.href);
          return;
        }
      }
    }

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    const optionValueId = selectedOption.dataset.optionValueId ?? '';
    if (optionValueId) this.dataset.currentSelectedOptionId = optionValueId;
    this.#captureSelectedState();
    this.#syncSizeControlAttributes();
    this.#refreshOptionLabels();
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    const requestUrl = this.buildRequestUrl(selectedOption);
    // When inside a product-card or quick-add, defer URL mutation to the card handler; just fetch and update UI
    this.fetchUpdatedSection(requestUrl, loadsNewProduct);

    // console.log('Variant changed to option value ID:', selectedOption);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    // Avoid mutating the main URL when the picker lives inside product-card or quick-add contexts
    const isCardContext = !!(this.closest('product-card') || this.closest('quick-add-component'));
    if (!isCardContext && url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLOptionElement) {
      const select = target.parentElement;
      if (select instanceof HTMLSelectElement) {
        for (const option of select.options) {
          option.removeAttribute('selected');
        }
        target.setAttribute('selected', 'selected');
        select.value = target.value;
      }
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // Set loading state to prevent premature add-to-cart actions
    this.dataset.variantLoading = 'true';

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        // console.log('Fetch successful for URL:', responseText);
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        // Find the JSON payload inside the same picker tag that initiated the request.
        // This supports both <variant-picker> (PDP) and <swatches-variant-picker-component> (cards/quick add).
        const rootSelector = this.tagName.toLowerCase();
        const textContent = html.querySelector(`${rootSelector} script[type="application/json"]`)?.textContent
          // Fallback for older markup that might still return variant-picker in the response
          ?? html.querySelector(`variant-picker script[type="application/json"]`)?.textContent
          // Fallback for swatches component explicitly, in case tag casing differs
          ?? html.querySelector(`swatches-variant-picker-component script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
          // Clear loading state after morph
          delete this.dataset.variantLoading;
        } else {
          const newProduct = this.updateVariantPicker(html);
          const textItem = document.getElementById(`${this.dataset?.productId}-color-label`);
          if (textItem && textItem.textContent) {
            textItem.textContent = this.selectedOption?.getAttribute('aria-label') ?? '';
          }

          // Reinitialize Swiper after DOM update
          this.reinitializeSwiper();

          // Clear loading state BEFORE dispatching event so the button can be enabled
          delete this.dataset.variantLoading;

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            console.log('Dispatching VariantUpdateEvent for option value ID:', this.selectedOptionId);
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })



            );

          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        // Clear loading state when fetch completes (success or error)
        delete this.dataset.variantLoading;
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    // Persist current selection in memory to survive morph cycles even if attributes are overwritten
    const persistedAllPre = this.dataset.currentSelectedOptions || '';
    const persistedIdPre = this.dataset.currentSelectedOptionId || '';

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // Pre-mark persisted selections on the incoming source to avoid a brief uncheck/recheck flicker during morph
    try {
      const idsToPreset = (persistedAllPre?.split(',').filter(Boolean) ?? []).length
        ? persistedAllPre.split(',').filter(Boolean)
        : (persistedIdPre ? [persistedIdPre] : []);
      for (const id of idsToPreset) {
        const radio = newVariantPickerSource.querySelector(`[data-option-value-id="${id}"]`);
        if (radio instanceof HTMLInputElement) {
          radio.checked = true;
          radio.setAttribute('checked', '');
        } else if (radio instanceof HTMLOptionElement) {
          radio.setAttribute('selected', 'selected');
          radio.parentElement && (radio.parentElement.value = radio.value);
        }
      }
    } catch (e) {
      // Ignore if not present in new source
    }

    morph(this, newVariantPickerSource);

    // Reapply the selection captured before morphing
    try {
      const idsToApply = (persistedAllPre?.split(',').filter(Boolean) ?? []).length
        ? persistedAllPre.split(',').filter(Boolean)
        : (persistedIdPre ? [persistedIdPre] : []);
      for (const id of idsToApply) {
        this.updateSelectedOption(id);
      }
    } catch (error) {
      // Ignore if ids are not present in the new markup
    }

    // Re-capture and refresh UI
    this.#captureSelectedState();
    this.#refreshOptionLabels();
    this.#syncSizeControlAttributes();

    // Clear (but never auto-pick) a size that became invalid after the update
    this.#clearInvalidSizeOnUpdate();

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * Persist the currently selected option value ids for reapplication after morphs.
   */
  #captureSelectedState() {
    try {
      const values = this.selectedOptionsValues;
      if (values.length) {
        this.dataset.currentSelectedOptions = values.join(',');
      } else {
        delete this.dataset.currentSelectedOptions;
      }
    } catch (error) {
      delete this.dataset.currentSelectedOptions;
    }
  }

  /**
   * Reapplies any persisted option selections after the DOM has been morphed.
   */
  #reapplyPersistedSelections() {
    const persistedAll = this.dataset.currentSelectedOptions?.split(',').filter(Boolean) ?? [];
    const idsToApply = persistedAll.length
      ? persistedAll
      : this.dataset.currentSelectedOptionId
        ? [this.dataset.currentSelectedOptionId]
        : [];

    for (const id of idsToApply) {
      try {
        this.updateSelectedOption(id);
      } catch (error) {
        // Option might not exist anymore; ignore.
      }
    }

    this.#captureSelectedState();
  }

  /**
   * Ensures required attributes on size controls stay in sync with the current selection state.
   */
  #syncSizeControlAttributes() {
    const sizeGroups = this.querySelectorAll('.variant-option[data-is-size-option="true"]');
    sizeGroups.forEach((group) => {
      const checkedRadio = group.querySelector('input[type="radio"]:checked');
      for (const radio of group.querySelectorAll('input[type="radio"]')) {
        if (!(radio instanceof HTMLInputElement)) continue;
        if (radio === checkedRadio) {
          radio.setAttribute('checked', '');
        } else {
          radio.removeAttribute('checked');
        }
      }

      const select = group.querySelector('select');
      if (select instanceof HTMLSelectElement) {
        const value = select.value;
        for (const option of Array.from(select.options)) {
          if (option.value === value && value !== '') {
            option.setAttribute('selected', 'selected');
          } else {
            option.removeAttribute('selected');
          }
        }
      }
    });
  }

  /**
   * Updates inline labels that surface the currently selected value.
   */
  #refreshOptionLabels() {
    const optionGroups = this.querySelectorAll('.variant-option');
    optionGroups.forEach((group) => {
      const target = group.querySelector('[data-selected-text-target]');
      if (!(target instanceof HTMLElement)) return;

      let displayText = '';
      const checkedRadio = group.querySelector('input[type="radio"]:checked');
      if (checkedRadio instanceof HTMLInputElement) {
        displayText = checkedRadio.getAttribute('aria-label') || checkedRadio.value || '';
        target.classList.remove('variant-option__swatch-value--error');
      }

      if (!displayText) {
        const select = group.querySelector('select');
        if (select instanceof HTMLSelectElement) {
          const selectedOption = select.selectedOptions[0];
          if (selectedOption && selectedOption.value !== '') {
            displayText = selectedOption.textContent?.trim() || '';
            target.classList.remove('variant-option__swatch-value--error');
          }
        }
      }

      if (!displayText) {
        displayText = target.getAttribute('data-placeholder') || '';
      }

      target.textContent = displayText;
    });
  }

  /**
   * Handles global variant update events.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  handleGlobalVariantUpdate(event) {
    // Only handle events for this product
    if (event.detail.data.productId === this.dataset.productId) {
      this.reinitializeSwiper();
      this.#refreshOptionLabels();
    }
  }

  /**
   * Slides to the active variant in all Swiper instances.
   */
  slideToActiveVariant() {
    const swiperElements = this.querySelectorAll('.variant-swiper');

    swiperElements.forEach((swiperElement) => {
      // @ts-ignore - swiper property is added by Swiper library
      if (swiperElement.swiper && typeof swiperElement.swiper.slideToActiveVariant === 'function') {
        // @ts-ignore - swiper property is added by Swiper library
        swiperElement.swiper.slideToActiveVariant();
      }
    });
  }

  /**
   * Reinitializes Swiper instances after DOM update.
   */
  reinitializeSwiper() {
    // Wait for DOM to be fully updated
    requestAnimationFrame(() => {
      const swiperElements = this.querySelectorAll('.variant-swiper');

      swiperElements.forEach((swiperElement) => {
        // Check if Swiper is available
        try {
          // @ts-ignore - Swiper is loaded externally
          if (typeof window !== 'undefined' && window.Swiper) {
            // Destroy existing instance if it exists
            // @ts-ignore - swiper property is added by Swiper library
            if (swiperElement.swiper && typeof swiperElement.swiper.destroy === 'function') {
              // @ts-ignore - swiper property is added by Swiper library
              swiperElement.swiper.destroy(true, true);
            }

            // Get the option type from the class (e.g., variant-swiper-color, variant-swiper-size)
            const optionClass = Array.from(swiperElement.classList).find(cls => cls.startsWith('variant-swiper-'));
            const optionType = optionClass ? optionClass.replace('variant-swiper-', '') : '';

            // Initialize new Swiper instance
            // @ts-ignore - Swiper is loaded externally
            const swiper = new window.Swiper(swiperElement, {
              slidesPerView: 'auto',
              spaceBetween: 10,
              freeMode: true,
              grabCursor: true,
              slideToClickedSlide: true,
              centeredSlides: false,
              navigation: {
                nextEl: `.swiper-button-next-${optionType}`,
                prevEl: `.swiper-button-prev-${optionType}`,
              },
              keyboard: {
                enabled: true,
                onlyInViewport: true,
              },
            });

            // Add method to slide to active variant
            swiper.slideToActiveVariant = function () {
              const activeSlide = this.el.querySelector('.swiper-slide input:checked');
              if (activeSlide) {
                const slideIndex = Array.from(this.slides).findIndex(slide =>
                  slide.contains(activeSlide)
                );
                if (slideIndex !== -1) {
                  this.slideTo(slideIndex, 300);
                }
              }
            };
          }
        } catch (error) {
          console.warn('Failed to reinitialize Swiper:', error);
        }
      });
    });
  }

  /**
   * After a variant update (e.g. color swap), uncheck a previously selected Size
   * if it's no longer available for the new selection. Never auto-picks a
   * replacement - the shopper must explicitly choose a size.
   */
  #clearInvalidSizeOnUpdate() {
    const sizeGroups = this.querySelectorAll('.variant-option[data-is-size-option="true"]');

    sizeGroups.forEach((group) => {
      const currentlyChecked = group.querySelector('input[type="radio"]:checked');
      const selectEl = group.querySelector('select.variant-option__select');

      if (currentlyChecked instanceof HTMLInputElement) {
        const isAvailable = currentlyChecked.dataset.optionAvailable === 'true';
        if (!isAvailable) {
          currentlyChecked.checked = false;
          currentlyChecked.removeAttribute('checked');
        }
      }

      if (selectEl instanceof HTMLSelectElement && selectEl.value !== '') {
        const selectedOption = selectEl.options[selectEl.selectedIndex];
        if (selectedOption && selectedOption.disabled) {
          selectEl.value = '';
        }
      }
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
